/**
 * LifecycleBadge — §33 ADR-119 Pattern Library universal.
 *
 * 6 patterns visuais canônicos:
 *   - initial            → cinza
 *   - in-progress        → warning (laranja) + animação spin opcional
 *   - completed-positive → success (verde)
 *   - completed-negative → danger (vermelho)
 *   - on-hold            → info (azul)
 *   - unclear            → AI (periwinkle)
 *
 * Cada entity Lifecycle ADR-105/108/109/115 mapeia seus estados para um
 * destes 6 patterns via tabela `stateToPattern` (origem r7a/scenario.jsx).
 */

export type LifecyclePattern =
  | 'initial'
  | 'in-progress'
  | 'completed-positive'
  | 'completed-negative'
  | 'on-hold'
  | 'unclear';

export interface LifecycleStateInfo {
  pattern: LifecyclePattern;
  label: string;
}

/** Tabela canonical r7a: 19 estados de 5 entities → 6 patterns + label PT-BR */
export const STATE_TO_PATTERN: Record<string, LifecycleStateInfo> = {
  /* CustomerCommitment ADR-105 */
  pending: { pattern: 'initial', label: 'Pendente' },
  fulfilled: { pattern: 'completed-positive', label: 'Cumprido' },
  broken: { pattern: 'completed-negative', label: 'Vencido' },
  cancelled: { pattern: 'completed-negative', label: 'Cancelado' },
  unclear: { pattern: 'unclear', label: 'Não esclarecido' },
  /* ConversationIntent ADR-108 */
  detected: { pattern: 'initial', label: 'Detectado' },
  suggested: { pattern: 'in-progress', label: 'Sugerido pela IA' },
  confirmed: { pattern: 'completed-positive', label: 'Confirmado' },
  refuted: { pattern: 'completed-negative', label: 'Refutado' },
  /* Pendency ADR-109 */
  open: { pattern: 'initial', label: 'Aberta' },
  'in-progress-pendency': { pattern: 'in-progress', label: 'Em progresso' },
  resolved: { pattern: 'completed-positive', label: 'Resolvida' },
  'cancelled-pendency': { pattern: 'completed-negative', label: 'Cancelada' },
  /* Appointment ADR-115 */
  tentative: { pattern: 'initial', label: 'Tentativa' },
  'confirmed-appointment': { pattern: 'initial', label: 'Confirmado' },
  'in-progress-appt': { pattern: 'in-progress', label: 'Em curso' },
  completed: { pattern: 'completed-positive', label: 'Concluído' },
  'cancelled-appt': { pattern: 'completed-negative', label: 'Cancelado' },
};

export function LifecycleBadge({
  state,
  confidence,
}: {
  state: string;
  confidence?: number | null;
}): JSX.Element {
  const info = STATE_TO_PATTERN[state] ?? { pattern: 'initial' as const, label: state };
  return (
    <span
      className={`lc-badge lc-badge--${info.pattern}`}
      data-testid={`lc-badge-${state}`}
      aria-label={`Estado: ${info.label}`}
    >
      {info.label}
      {confidence !== null && confidence !== undefined && (
        <span className="lc-badge__confidence">{Math.round(confidence * 100).toString()}%</span>
      )}
    </span>
  );
}
